import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Button,
  ButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Timeline,
  TrendingUp,
  TrendingDown,
  Assessment,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

// Mock data for charts
const salesData = [
  { month: 'Jan', sales: 85000, orders: 240, profit: 25000 },
  { month: 'Feb', sales: 72000, orders: 198, profit: 21000 },
  { month: 'Mar', sales: 98000, orders: 300, profit: 31000 },
  { month: 'Apr', sales: 64000, orders: 208, profit: 19000 },
  { month: 'May', sales: 55000, orders: 198, profit: 20000 },
  { month: 'Jun', sales: 67000, orders: 260, profit: 21000 },
];

const categoryData = [
  { name: 'Brakes & Suspension', value: 400 },
  { name: 'Engine Parts', value: 300 },
  { name: 'Electrical', value: 250 },
  { name: 'Service Items', value: 200 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const topProducts = [
  { id: 1, name: 'Brake Pad Set - Toyota Hilux', sales: 145, revenue: 217500, growth: 23 },
  { id: 2, name: 'Oil Filter - VW Golf', sales: 125, revenue: 31250, growth: 15 },
  { id: 3, name: 'Willard Battery 643', sales: 98, revenue: 186200, growth: -5 },
  { id: 4, name: 'Air Filter - Toyota Corolla', sales: 89, revenue: 22250, growth: 8 },
];

interface PieChartLabelProps {
  name: string;
  percent: number;
}

export const Analytics: React.FC = () => {
  const [timeRange, setTimeRange] = useState('month');
  const [chartType, setChartType] = useState('sales');

  const getChartData = () => {
    switch (chartType) {
      case 'sales':
        return (
          <LineChart data={salesData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="sales"
              stroke="#8884d8"
              activeDot={{ r: 8 }}
            />
            <Line type="monotone" dataKey="profit" stroke="#82ca9d" />
          </LineChart>
        );
      case 'orders':
        return (
          <BarChart data={salesData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="orders" fill="#8884d8" />
          </BarChart>
        );
      case 'categories':
        return (
          <PieChart>
            <Pie
              data={categoryData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }: PieChartLabelProps) => `${name} ${(percent * 100).toFixed(0)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {categoryData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        );
      default:
        return (
          <LineChart data={salesData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="sales"
              stroke="#8884d8"
              activeDot={{ r: 8 }}
            />
            <Line type="monotone" dataKey="profit" stroke="#82ca9d" />
          </LineChart>
        );
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Analytics & Reports
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary">
                  Total Revenue
                </Typography>
                <Typography variant="h4" sx={{ mt: 1 }}>
                  R 441,200
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}
                >
                  <TrendingUp sx={{ mr: 1 }} />
                  +15% vs last month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary">
                  Total Orders
                </Typography>
                <Typography variant="h4" sx={{ mt: 1 }}>
                  1,234
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}
                >
                  <TrendingUp sx={{ mr: 1 }} />
                  +8% vs last month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary">
                  Average Order Value
                </Typography>
                <Typography variant="h4" sx={{ mt: 1 }}>
                  R 3,670
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'error.main', display: 'flex', alignItems: 'center' }}
                >
                  <TrendingDown sx={{ mr: 1 }} />
                  -3% vs last month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary">
                  Customer Satisfaction
                </Typography>
                <Typography variant="h4" sx={{ mt: 1 }}>
                  4.8/5
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}
                >
                  <TrendingUp sx={{ mr: 1 }} />
                  +0.3 vs last month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
                <ButtonGroup variant="outlined" size="small">
                  <Button
                    variant={chartType === 'sales' ? 'contained' : 'outlined'}
                    onClick={() => setChartType('sales')}
                  >
                    Sales & Profit
                  </Button>
                  <Button
                    variant={chartType === 'orders' ? 'contained' : 'outlined'}
                    onClick={() => setChartType('orders')}
                  >
                    Orders
                  </Button>
                  <Button
                    variant={chartType === 'categories' ? 'contained' : 'outlined'}
                    onClick={() => setChartType('categories')}
                  >
                    Categories
                  </Button>
                </ButtonGroup>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Time Range</InputLabel>
                  <Select
                    value={timeRange}
                    label="Time Range"
                    onChange={(e) => setTimeRange(e.target.value)}
                  >
                    <MenuItem value="week">Last Week</MenuItem>
                    <MenuItem value="month">Last Month</MenuItem>
                    <MenuItem value="year">Last Year</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  {getChartData()}
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Top Performing Products
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">Growth</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topProducts.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>{product.name}</TableCell>
                        <TableCell align="right">R {product.revenue.toLocaleString()}</TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: product.growth >= 0 ? 'success.main' : 'error.main',
                          }}
                        >
                          {product.growth >= 0 ? '+' : ''}
                          {product.growth}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
}; 